import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { AuthShell } from "@/pages/auth/Login";
import {
  IconHomeHeart, IconSchool, IconFriends, IconStethoscope, IconBuildingCommunity,
} from "@tabler/icons-react";

const ROLES = [
  { id: "familia", icon: <IconHomeHeart size={22} /> },
  { id: "docente", icon: <IconSchool size={22} /> },
  { id: "sombra", icon: <IconFriends size={22} /> },
  { id: "psicologa", icon: <IconStethoscope size={22} /> },
  { id: "escuela", icon: <IconBuildingCommunity size={22} /> },
];

export default function Register() {
  const { t, lang } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get("email") || "";
  const emailLocked = searchParams.get("locked") === "1" && !!prefilledEmail;
  const [role, setRole] = useState("familia");
  const [form, setForm] = useState({ name: "", email: prefilledEmail, password: "", gender: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const r = await register({ ...form, gender: form.gender || null, role, lang });
    setLoading(false);
    if (r.ok) {
      let next = searchParams.get("next");
      if (!next) { try { next = sessionStorage.getItem("chiessa.postLogin"); } catch { /* noop */ } }
      try { sessionStorage.removeItem("chiessa.postLogin"); } catch { /* noop */ }
      nav(next && next.startsWith("/") ? next : "/app");
    }
    else setError(r.error);
  };

  return (
    <AuthShell>
      <p className="eyebrow">{t("nav.register")}</p>
      <h1 className="font-display font-medium text-[34px] mt-2 leading-tight">
        {lang === "es" ? "Crea tu cuenta en el instituto" : "Create your institute account"}
      </h1>

      <div className="mt-8">
        <p className="lbl m-0 mb-3">{t("auth.role")}</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {ROLES.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRole(r.id)}
              className={`mod-card text-center py-4 px-3 ${role === r.id ? "border-teal bg-teal-soft" : ""}`}
              data-testid={`register-role-${r.id}`}
            >
              <div className={`w-10 h-10 mx-auto rounded-lg flex items-center justify-center ${role === r.id ? "bg-teal text-white" : "bg-teal-soft text-teal-deep"}`}>
                {r.icon}
              </div>
              <p className="text-[12.5px] mt-2 font-medium">{t(`roles.${r.id}`)}</p>
            </button>
          ))}
        </div>
      </div>

      <form className="mt-8 space-y-4 max-w-[420px]" onSubmit={submit}>
        <div className="field">
          <label>{t("auth.name")}</label>
          <input
            className="input"
            required
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            data-testid="register-name"
          />
        </div>
        <div className="field">
          <label>{lang === "es" ? "Género" : "Gender"} <span className="text-tert text-[11px] font-normal">({lang === "es" ? "opcional" : "optional"})</span></label>
          <select
            className="input"
            value={form.gender}
            onChange={(e) => set("gender", e.target.value)}
            data-testid="register-gender"
          >
            <option value="">{lang === "es" ? "Prefiero no decir" : "Prefer not to say"}</option>
            <option value="femenino">{lang === "es" ? "Femenino" : "Female"}</option>
            <option value="masculino">{lang === "es" ? "Masculino" : "Male"}</option>
            <option value="otro">{lang === "es" ? "Otro" : "Other"}</option>
            <option value="prefiero_no_decir">{lang === "es" ? "Prefiero no decir" : "Prefer not to say"}</option>
          </select>
          <p className="text-tert text-[11px] mt-1">
            {lang === "es"
              ? "Solo se usa para dirigirnos a ti correctamente (Bienvenido / Bienvenida)."
              : "Only used to greet you correctly."}
          </p>
        </div>
        <div className="field">
          <label>{t("auth.email")}</label>
          <input
            className="input"
            type="email"
            required
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            readOnly={emailLocked}
            data-testid="register-email"
          />
          {emailLocked && (
            <p className="text-tert text-[11.5px] mt-1" data-testid="register-email-locked-hint">
              {lang === "es"
                ? "Tu invitación al círculo está vinculada a este correo. No podés cambiarlo aquí."
                : "Your circle invitation is linked to this email. You can't change it here."}
            </p>
          )}
        </div>
        <div className="field">
          <label>{t("auth.password")}</label>
          <input
            className="input"
            type="password"
            required
            minLength={6}
            value={form.password}
            onChange={(e) => set("password", e.target.value)}
            data-testid="register-password"
          />
        </div>
        {error && <p className="text-errtext text-[13px]" data-testid="register-error">{error}</p>}
        <button type="submit" className="btn w-full justify-center" disabled={loading} data-testid="register-submit">
          {loading ? "..." : t("auth.submit_register")}
        </button>
      </form>

      <div className="mt-6 text-[13.5px]">
        {t("auth.have_account")} {" "}
        <Link to="/login" className="text-teal font-medium underline underline-offset-4" data-testid="link-login">
          {t("auth.login_here")}
        </Link>
      </div>
    </AuthShell>
  );
}
